<template>
  <div class="profile-input-wrapper profile-input-phone-number">
    <profile-form
      ref="form"
      :method="method"
      endpoint="phonenumbers"
      @error="onError($event)"
      @external-error="openClosedAccordions"
    >
      <form class="form">
        <fieldset
          v-for="(localPhoneNumber, fieldsetIndex) in this.localProfile
            .phoneNumbers"
          :key="'fieldset' + fieldsetIndex"
          style="padding: 0"
        >
          <legend class="sr-only">{{ inputTitle }}</legend>
          <accordion-tab ref="accordionTab">
            <li slot-scope="{ toggleTab, expanded }" class="accordion-item">
              <button
                :id="'accordion-control' + fieldsetIndex"
                class="accordion-item-label"
                :class="expanded ? 'expanded' : ''"
                :aria-controls="'content' + fieldsetIndex"
                :aria-expanded="expanded ? 'true' : 'false'"
                v-on:click.prevent="toggleTab"
                v-on:enter.prevent="toggleTab"
                v-on:keydown.space.prevent="toggleTab"
              >
                <template v-if="allFieldsFilled(fieldsetIndex)">
                  {{ getTechTypeLabel(localPhoneNumber.type) }} -
                  {{ localPhoneNumber.number.slice(-3) }}
                </template>
                <template v-else> {{ tc("booking.newAddress") }} {{ tc("profile.formFields.phoneNumber") }} </template>
                <span class="icon-chevron-down"></span>
              </button>

              <div
                :id="'content' + fieldsetIndex"
                class="accordion-item-content"
                :aria-hidden="expanded ? 'false' : 'true'"
              >
                <div class="accordion-item-content-wrapper">
                  <div
                    class="input radio"
                    @keydown="handleKeyDown($event, fieldsetIndex)">
                    <label v-for="(techType, index) in techTypes" :key="techType.value"
                    :class="[{'disabled': isSelected(techType.value)}]">
                      <input
                        v-model="localPhoneNumber.type"
                        type="radio"
                        :ref="'radioButton-' + fieldsetIndex + '-' + index"
                        :class="[
                          'req',
                          { 'no-value': !localPhoneNumber.type },
                          { valid: !!localPhoneNumber.type },
                          { 'selected': isSelected(techType.value) },
                        ]"
                        :name="'type' + fieldsetIndex"
                        :id="techType.value + fieldsetIndex"
                        :value="techType.value"
                        data-val="true"
                        :data-val-required="getValidationForField('required')"
                        :aria-label="isSelected(techType.value) ? `${techType.label} ${isSelectedLabel}` : techType.label"
                      />
                      <input
                        v-if="localPhoneNumber.type === techType.value"
                        type="hidden"
                        :name="'techType' + fieldsetIndex"
                        :id="techType.label + fieldsetIndex"
                        :value="techType.label"
                      />
                      {{ techType.label }}
                    </label>
                    <span
                      :data-valmsg-for="'techType' + fieldsetIndex"
                      data-valmsg-replace="true"
                      class="field-validation-valid"
                    ></span>
                  </div>
                  <div class="flex-grid-3-col gutter-sm">
                    <div class="input dropdown phone-country">
                      <phone-country-select>
                        <template #default="{ handleSelectChange }">
                          <select
                            v-model="localPhoneNumber.countryCode"
                            :class="[
                              { 'no-value': !localPhoneNumber.countryCode },
                              { valid: !!localPhoneNumber.countryCode },
                            ]"
                            :id="'phoneCountryCode' + fieldsetIndex"
                            :name="'countryCode' + fieldsetIndex"
                            class="req"
                            data-val-external="phone"
                            :data-external-msg="getValidationForField('externalPhone')"
                            data-val="true"
                            :data-val-required="
                              getValidationForField('required', 'phoneCountryCode')
                            "
                            data-field-type="DropdownList"
                            @change="handleSelectChange"
                          >
                            <option value=""></option>
                            <option
                              v-for="country in countries"
                              :key="country.Name + 'Phone'"
                              :value="'+' + country.CallingCode"
                              :data-code="`+${country.CallingCode}`"
                              :data-country="country.Name"
                            >
                              {{ `${country.Name} (+${country.CallingCode})` }}
                            </option>
                          </select>
                        </template>
                      </phone-country-select>
                      <label
                        :for="'phoneCountryCode' + fieldsetIndex"
                        class="req floating select-label"
                      >
                        {{ tc("profile.formFields.phoneCountryCode") }}
                      </label>
                      <span
                        :data-valmsg-for="'phoneCountryCode' + fieldsetIndex"
                        data-valmsg-replace="true"
                        class="field-validation-valid"
                      ></span>
                    </div>
                    <div class="input phone phone-number">
                      <input
                        v-model="localPhoneNumber.number"
                        @input="localPhoneNumber.number = localPhoneNumber.number.replace(/[^0-9]/g, '').replace(/(\..*?)\..*/g, '$1');"
                        :class="[
                          { 'no-value': !localPhoneNumber.number },
                          { valid: !!localPhoneNumber.number },
                        ]"
                        class="req"
                        type="tel"
                        :id="'number' + fieldsetIndex"
                        :name="'number' + fieldsetIndex"
                        data-val-external="phone"
                        :data-external-msg="getValidationForField('externalPhone')"
                        data-val="true"
                        :data-val-required="getValidationForField('required', 'phoneNumber')"
                        data-rule-minlength="5"
                        data-rule-maxlength="17"
                        :data-msg-minlength="getValidationForField('minChars', '5')"
                        :data-msg-maxlength="getValidationForField('maxChars', '17')"
                        :data-val-phone-number="tc('validations.phone')"
                        data-field-type="PhoneNumber"
                      />
                      <label
                        :for="'number' + fieldsetIndex"
                        class="req floating input-label"
                      >
                        {{ tc("profile.formFields.phoneNumber") }}
                      </label>
                      <span
                        :data-valmsg-for="'number' + fieldsetIndex"
                        data-valmsg-replace="true"
                        class="field-validation-valid"
                      ></span>
                    </div>
                  </div>
                  <div class="phone-remove">
                    <button
                      v-if="showRemoveBtn(fieldsetIndex)"
                      class="phone-remove-btn"
                      @click.prevent="removePhoneNumber(fieldsetIndex)"
                    >
                      <span class="code-text">{{ tc("booking.remove") }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </li>
          </accordion-tab>
        </fieldset>
        <button
          v-if="phoneNumbersLength < 3 && allFieldsFilled(0)"
          class="phone-add"
          @click.prevent="initNewPhoneNumber"
        >
          <div class="icon-add-gold"></div>
          <span class="code-text"
            >{{ tc("booking.add") }} {{ tc("profile.formFields.phoneNumber") }}</span
          >
        </button>
        <input type="hidden" :value="true" name="isPhoneNumbers" />
      </form>
    </profile-form>

    <div class="profile-info">
      <div class="profile-info-icon icon-info"></div>
      <div class="profile-info-section">
        <div class="profile-info-section-title">
          {{ tc("profile.dataUseInfo.title") }}
        </div>
        <div class="profile-info-section-description">
          {{ tc("profile.dataUseInfo.description.phoneNumber") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ProfileInputMixin from '@mixins/profile-input-mixin'
  import { KEY_CODE_MAP } from '@utils/keyboard-helpers'
  import PhoneCountrySelect from '@components/01.base/01.forms/select/phone-country-select'

  export default {
    name: 'profile-phone-input',
    components: {
      PhoneCountrySelect,
    },
    mixins: [ProfileInputMixin],
    props: {
      countries: {
        type: Array,
        required: true,
      },
      techTypes: {
        type: Array,
        required: true,
      },
      isSelectedLabel: {
        type: String,
        default: 'is selected',
      },
    },
    data: () => ({
      localProfile: {
        phoneNumbers: [],
      },
    }),
    mounted() {
      this.configureModalStyle()
      if (this.phoneNumbersLength === 0) {
        this.initNewPhoneNumber()
      } else {
        this.$nextTick(() => {
          this.$refs.form.onExternalValidation({ data: { validationType: 'phone' }})
        })
      }
      this.$nextTick(() => {
        this.openClosedAccordions()
      })
    },
    computed: {
      phoneNumbersLength() {
        return this.localProfile.phoneNumbers
          ? this.localProfile.phoneNumbers.length
          : 0
      },
      method() {
        return 'post'
      },
    },
    methods: {
      resetValidations() {
        this.$nextTick(() => {
          if(this.$refs.form) {
            this.$refs.form.reInitForm()
          }
        })
      },
      onError(e) {
        this.openClosedAccordions()
        this.$emit('done', e)
      },
      openClosedAccordions() {
        this.resetValidations()
        if (this.$refs.accordionTab) {
          this.$refs.accordionTab.forEach((el) => {
            if (!el.expanded) {
              el.toggleTab()
            }
          })
        }
      },
      openLastAccordion(){
        this.resetValidations()
        if(this.$refs.accordionTab) {
          if(!this.$refs.accordionTab[this.phoneNumbersLength - 1].expanded){
            this.$refs.accordionTab[this.phoneNumbersLength - 1].toggleTab()
          }
        }
      },
      configureModalStyle() {
        const modalBody = document.querySelector('.modal-body')
        modalBody.style.padding = '0 40px 0 40px'
      },
      pushEmptyPhoneNumber() {
        this.localProfile.phoneNumbers.push({
          type: null,
          countryCode: null,
          number: '',
          isNewPhone: true,
        })
      },
      initNewPhoneNumber() {
        this.pushEmptyPhoneNumber()
        if (this.$refs.form) {
          this.$nextTick(() => {
            this.$refs.form.getExternalValidations()
            this.$refs.form.reInitForm()
            this.openLastAccordion()
          })
        }
      },
      async removePhoneNumber(index) {
        this.isLoading = true
        this.localProfile.phoneNumbers.splice(index, 1)
        if (!this.userPhoneNumber(index).isNewPhone) {
          this.$refs.form.form.submit()
        }
        this.isLoading = false
      },
      userPhoneNumber(i) {
        return this.localProfile.phoneNumbers[i] || {}
      },
      allFieldsFilled(index = '') {
        const neededFields = ['type', 'countryCode', 'number']
        let haveNeededFields = false
        if (index.toString()) {
          haveNeededFields = this.localProfile.phoneNumbers[index]
            && neededFields
              .map((f) => (Array.isArray(this.localProfile.phoneNumbers[index][f])
                ? this.localProfile.phoneNumbers[index][f].length > 0
                : !!this.localProfile.phoneNumbers[index][f]))
              .filter((f) => !!f).length === neededFields.length
        }
        return haveNeededFields
      },
      isSelected(type) {
        return this.localProfile.phoneNumbers.some((phone) => {
          if (Number(phone.type) === Number(type)) {
            return true
          }
        })
      },
      getTechTypeLabel(type) {
        return this.techTypes.find(el => Number(el.value) === Number(type))?.label
      },
       showRemoveBtn(index) {
        const hasMoreThanOnePhone = this.phoneNumbersLength > 1
        const isNewPhone = !!this.localProfile.phoneNumbers[index]?.isNewPhone
        const allPhonesAreSaved = this.localProfile.phoneNumbers.map((phone) => { return !!phone?.isNewPhone === false })
        .filter((f) => { return !!f}).length === this.phoneNumbersLength

        return hasMoreThanOnePhone && (isNewPhone || allPhonesAreSaved)
      },
      handleKeyDown(e, fieldsetIndex) {
        const focusedElement = document.activeElement;
        if (focusedElement.tagName === 'INPUT' && focusedElement.type === 'radio') {
          const focusedIndex = this.getFocusedIndex(focusedElement);
          switch (e.keyCode) {
            case KEY_CODE_MAP.ARROW_UP:
            case KEY_CODE_MAP.ARROW_LEFT:
              e.preventDefault();
              this.focusEnabledRadioButton(focusedIndex, fieldsetIndex, 'previous')
              break;
            case KEY_CODE_MAP.ARROW_DOWN:
            case KEY_CODE_MAP.ARROW_RIGHT:
              e.preventDefault();
              this.focusEnabledRadioButton(focusedIndex, fieldsetIndex, 'next')
              break;
          }
        }
      },
      getFocusedIndex(element) {
        for (const refKey of Object.keys(this.$refs)) {
          const refElement = this.$refs[refKey];
          if (Array.isArray(refElement) && refElement.includes(element)) {
            const index = refKey.split('-')[2];
            return parseInt(index);
          }
        }
        return -1;
      },
      focusEnabledRadioButton(index, fieldsetIndex, direction) {
        let i;
        if (direction === 'next') {
          i = index + 1;
          if (i >= this.techTypes.length) {
            i = 0;
          }
        } else if (direction === 'previous') {
          i = index - 1;
          if (i < 0) {
            i = this.techTypes.length - 1;
          }
        } else {
          // Handle invalid direction parameter
          return;
        }
        while (i !== index) {
          const radioButton = this.$refs['radioButton-' + fieldsetIndex + '-' + i];
          if (!radioButton[0].classList.contains('selected')) {
            radioButton[0].focus();
            break;
          }
          if (direction === 'next') {
            i = (i + 1) % this.techTypes.length;
          } else if (direction === 'previous') {
            i = (i - 1 + this.techTypes.length) % this.techTypes.length;
          }
        }
      },
    },
  }
</script>
